import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Settings2, Wind, Sparkles } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/site";

const SECTIONS = [
  {
    num: "01",
    icon: Zap,
    title: "Engine Upgrades",
    desc: "Downpipes, intercoolers, intake systems, and turbo upgrades — installed and validated on everything from S58 and B58 platforms to American V8s.",
    tags: ["Downpipes", "Intercoolers", "Turbo Upgrades", "Intakes"],
    image: IMG.mEngine,
  },
  {
    num: "02",
    icon: Settings2,
    title: "Suspension Setups",
    desc: "Coilovers, sway bars, bushings, and corner balancing. Dialed-in ride height and alignment for street, canyon, or track.",
    tags: ["Coilovers", "Alignments", "Bushings", "Sway Bars"],
    image: IMG.repairBay,
  },
  {
    num: "03",
    icon: Wind,
    title: "Exhaust Systems",
    desc: "Cat-back and turbo-back systems, valved setups, and custom fabrication. The sound your car should have had from the factory.",
    tags: ["Cat-Back", "Valved Exhaust", "Custom Fab", "Downpipes"],
    image: IMG.perfParts,
  },
  {
    num: "04",
    icon: Sparkles,
    title: "Appearance Upgrades",
    desc: "Wheels, aero, carbon accents, and stance. Complete the build with the same precision that went into the power.",
    tags: ["Wheels", "Aero", "Carbon", "Stance"],
    image: IMG.carNight,
  },
];

const PLATFORMS = ["European Vehicles", "Muscle Cars", "Turbocharged Platforms", "Custom Modifications"];

export default function Performance() {
  useEffect(() => {
    document.title = "Performance Shop | TRX Auto Sales & Service Inc — Performance Shop Charlotte NC";
  }, []);

  return (
    <>
      <PageHero
        eyebrow="TRX Performance"
        lines={["Built For", "Performance."]}
        sub="Performance upgrades for European vehicles, muscle cars, and turbocharged platforms — from bolt-ons to full custom builds."
        image={IMG.engineBay}
      />

      <section className="border-b border-white/10 py-10" data-testid="performance-platforms">
        <div className="mx-auto flex max-w-[1600px] flex-wrap gap-3 px-6 md:px-10">
          {PLATFORMS.map((p, i) => (
            <Reveal key={p} delay={i * 0.08} y={20}>
              <span className="inline-block border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-300">
                {p}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-8 md:py-16" data-testid="performance-sections">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          {SECTIONS.map((s, i) => (
            <div
              key={s.num}
              className={`grid items-center gap-10 border-b border-white/10 py-16 md:py-24 lg:grid-cols-2 lg:gap-20 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
              data-testid={`performance-${s.num}`}
            >
              <Reveal>
                <div className="group relative overflow-hidden border border-white/10">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-5 left-5 font-display text-6xl font-black text-white/15">{s.num}</span>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="flex items-center gap-4">
                  <s.icon className="h-6 w-6 text-trx-blue" />
                  <span className="font-display text-sm font-bold tracking-[0.3em] text-trx-blue">{s.num}</span>
                </div>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">
                  {s.title}
                </h2>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-400">{s.desc}</p>
                <ul className="mt-7 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <li key={t} className="border border-white/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400">
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-32" data-testid="performance-cta">
        <div className="absolute inset-0 spotlight" />
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal>
            <h2 className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tighter text-white">
              Have A Build In Mind?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
              Bring us your goals — street, strip, or show — and we'll engineer the path to get there.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/service"
              data-testid="performance-book-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black"
            >
              Start Your Build <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
